import { Badge, Box, Checkbox, Flex, SegmentedControl, Text } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import React from "react";
import { useSelector } from "react-redux";
import { LbfReceivableApi } from "../../apis";
import { IGeneral } from "../../interfaces/IGeneral";
import { IReceivable } from "../../interfaces/IReceivable";
import { IReference } from "../../models/General";
import { tugrug } from "../../utils";
import { PageFilter } from "../layout";
import { ColumnType, ITableRef, Table } from "../table";
import RenderText from "../ui/render-text";

const limitType = [
  {
    code: "BUYER_LED",
    name: "BUYER LED",
    color: "black",
  },
  {
    code: "SUPPLIER_LED",
    name: "SUPPLIER LED",
    color: "red",
  },
];

export function ReceivableAnchorList() {
  const ref = React.useRef<ITableRef>(null);
  const { lbfProgramStatus } = useSelector((state: { general: IGeneral }) => state.general);
  const [status, setStatus] = React.useState("");
  const [statusDebounced] = useDebouncedValue(status, 400);
  const [program, setProgram] = React.useState("");
  const [programDebounced] = useDebouncedValue(program, 400);

  const columns = useHeader({
    lbfProgramStatus,
  });

  return (
    <>
      <PageFilter
        left={[
          <Flex justify="flex-start" align="flex-start" direction="column" wrap="wrap" key={1}>
            <Text size={14} color="#868E96">
              Хөтөлбөрийн төрөл
            </Text>
            <SegmentedControl
              key={1}
              onChange={(e) => setStatus(e)}
              value={status}
              data={[{ name: "Бүгд", code: "", color: "yellow" }, ...limitType].map((item: IReference, index: number) => {
                return {
                  value: item.code || "",
                  label: (
                    <Badge variant="dot" color={item.color} style={{ border: "none" }} key={`${index}`}>
                      {item.name}
                    </Badge>
                  ),
                };
              })}
            />
          </Flex>,
          <Flex justify="flex-start" align="flex-start" direction="column" wrap="wrap" key={2}>
            <Text size={14} color="#868E96">
              Хөтөлбөрийн статус
            </Text>
            <SegmentedControl
              key={1}
              onChange={(e) => setProgram(e)}
              value={program}
              data={[{ name: "Бүгд", code: "", color: "yellow" }, ...lbfProgramStatus]?.map((item: IReference, index: number) => {
                return {
                  value: item.code || "",
                  label: (
                    <Badge variant="dot" color={item.color} style={{ border: "none" }} key={`${index}`}>
                      {item.name}
                    </Badge>
                  ),
                };
              })}
            />
          </Flex>,
        ]}
      />
      <Table
        ref={ref}
        name="lbf-receivable-anchor-limit.list"
        columns={columns}
        filters={{
          lbfProductType: statusDebounced,
          status: programDebounced,
        }}
        loadData={(data) => LbfReceivableApi.list(data!)}
      />
    </>
  );
}

const useHeader = ({ lbfProgramStatus }: { lbfProgramStatus: IReference[] }): ColumnType<IReceivable>[] => [
  {
    title: "#",
    width: "30px",
    render: (_r, index) => <RenderText text={index + 1} />,
  },
  {
    title: "Хөтөлбөрийн нэр",
    sorter: true,
    dataIndex: "",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.refCode || "-"} />
          <RenderText text={record?.name || "-"} />
        </Box>
      );
    },
  },
  {
    title: "Хөтөлбөр статус",
    sorter: true,
    dataIndex: "name",
    render: (record) => {
      return (
        <Badge
          size="md"
          radius="sm"
          variant="outline"
          color={(lbfProgramStatus || []).find((item: IReference) => item.code === record.lbfProgramStatus)?.color}>
          {(lbfProgramStatus || []).find((item: IReference) => item.code === record.lbfProgramStatus)?.name ?? "-"}
        </Badge>
      );
    },
  },
  {
    title: "Supplier",
    sorter: true,
    dataIndex: "name",
    render: (record) => {
      return (
        <Checkbox
          checked={record.lbfProductType === "SUPPLIER_LED" ? true : false}
          label={
            <Text size="sm" weight={500} c="#44566C">
              {record.lbfProductType === "SUPPLIER_LED" ? "Тийм" : "Үгүй"}
            </Text>
          }
          size="sm"
          onChange={() => {}}
        />
      );
    },
  },
  {
    title: "Buyer",
    sorter: true,
    dataIndex: "name",
    render: (record) => {
      return (
        <Checkbox
          checked={record.lbfProductType === "BUYER_LED" ? true : false}
          label={
            <Text size="sm" weight={500} c="#44566C">
              {record.lbfProductType === "BUYER_LED" ? "Тийм" : "Үгүй"}
            </Text>
          }
          size="sm"
          onChange={() => {}}
        />
      );
    },
  },
  {
    title: "Анкор партнер",
    sorter: true,
    dataIndex: "name",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.lbfAnchor?.partner?.businessName || "-"} />
          <RenderText text={record?.lbfAnchor?.partner?.refCode || "-"} color="#3e166e" />
        </Box>
      );
    },
  },
  {
    title: "Анкор бизнес",
    sorter: true,
    dataIndex: "name",
    render: (record) => {
      return (
        <Box>
          <RenderText text={record?.lbfAnchor?.profileName || "-"} />
          <RenderText text={record?.lbfAnchor?.refCode || "-"} color="#3e166e" />
        </Box>
      );
    },
  },
  {
    title: "CIF",
    sorter: true,
    dataIndex: "name",
    render: (record) => {
      return <RenderText text={record?.lbfAnchor?.clientCode || "-"} />;
    },
  },
  {
    title: "Баталсан лимит",
    sorter: true,
    dataIndex: "",
    render: (record) => {
      return <RenderText text={tugrug(record?.lbfProgramLimit || 0)} isAmount />;
    },
  },
  {
    title: "Боломжит лимит",
    sorter: true,
    dataIndex: "availableAmount",
    render: (record) => {
      return <RenderText text={tugrug(record.availableAmount || 0)} isAmount />;
    },
  },
  {
    title: "Ашигласан лимит",
    sorter: true,
    dataIndex: "utilizedAmount",
    render: (record) => {
      return <RenderText text={tugrug(record.utilizedAmount || 0)} isAmount />;
    },
  },
  {
    title: "Хүсэлт гаргасан",
    sorter: true,
    dataIndex: "",
    render: (record) => {
      return <RenderText text={tugrug(record.requestedAmount || 0)} isAmount />;
    },
  },
  {
    title: "Олголт хүлээж буй",
    sorter: true,
    dataIndex: "toDisburseAmount",
    render: (record) => {
      return <RenderText text={tugrug(record.toDisburseAmount || 0)} isAmount />;
    },
  },
];
